/**
 * Capture / apply for the unified persistence document.
 *
 * `captureSceneDocument` walks live `AppState` into a `RawSceneDocument`;
 * `applySceneDocument` patches state from one. The raw form holds live
 * `SceneObject` references so it can cross the worker boundary via structured
 * clone. The worker calls `encodeSceneObject` from `codec.ts` to base64
 * the typed arrays before `JSON.stringify`; the inverse path runs decode
 * in the worker before this module patches `AppState`.
 */

import {
  type SceneObjectValidationIssue,
  validateSceneObject,
} from "../../modeling/sceneObjectValidation.ts";
import type { MaterialDataBlock, ShadingMode } from "../../modeling/types.ts";
import { regenerateCurveGeometryInMap } from "../curveRegen.ts";
import {
  DEFAULT_EXPORT_QUALITY_OVERRIDES,
  DEFAULT_RENDER_RENDER_CONFIG,
  DEFAULT_VIEWPORT_QUALITY,
  DEFAULT_VIEWPORT_RENDER_CONFIG,
} from "../renderConfig.ts";
import { restoreSceneObjectMap } from "../restoreSceneObjects.ts";
import type { AppState, Collection, ModifierOverride } from "../storeTypes.ts";
import {
  decodeCollectionGeneratorRef,
  encodeCollectionGeneratorRef,
} from "./codec.ts";
import {
  POSEREF_MAGIC,
  POSEREF_VERSION,
  type RawSceneDocument,
  type RawSessionDocument,
  type SerializedBlendFileRef,
  type SerializedCollection,
  type SerializedCollectionCutterRef,
  type SerializedExportQualityOverrides,
  type SerializedGroupTransform,
  type SerializedReferenceLayer,
  type SerializedRenderConfig,
  type SerializedViewportQuality,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Render-config validation
// ---------------------------------------------------------------------------

/**
 * Ensure the document has a valid `renderConfigs` / `viewportQuality` /
 * `exportQualityOverrides` shape. A truncated or hand-edited document can
 * miss or corrupt individual fields — we fall back to per-scope defaults.
 * The user just sees a freshly-initialised render setup, which is the
 * correct failure mode for a disposable UI preference.
 */
function ensureRenderConfigs(doc: RawSceneDocument | RawSessionDocument): void {
  const asRecord = doc as unknown as {
    renderConfigs?: {
      viewport?: SerializedRenderConfig;
      render?: SerializedRenderConfig;
    };
    viewportQuality?: SerializedViewportQuality;
    exportQualityOverrides?: SerializedExportQualityOverrides;
  };
  asRecord.renderConfigs = {
    viewport: {
      ...DEFAULT_VIEWPORT_RENDER_CONFIG,
      ...asRecord.renderConfigs?.viewport,
    },
    render: {
      ...DEFAULT_RENDER_RENDER_CONFIG,
      ...asRecord.renderConfigs?.render,
    },
  };
  asRecord.viewportQuality = {
    ...DEFAULT_VIEWPORT_QUALITY,
    ...asRecord.viewportQuality,
  };
  asRecord.exportQualityOverrides = {
    ...DEFAULT_EXPORT_QUALITY_OVERRIDES,
    ...asRecord.exportQualityOverrides,
  };
}

// ---------------------------------------------------------------------------
// Shared helpers — Map ↔ Record conversions
// ---------------------------------------------------------------------------

function recordFromMap<V>(m: Map<string, V>): Record<string, V> {
  const out: Record<string, V> = {};
  for (const [k, v] of m) out[k] = v;
  return out;
}

function nestedRecordFromMap<V>(
  m: Map<string, Map<string, V>>,
): Record<string, Record<string, V>> {
  const out: Record<string, Record<string, V>> = {};
  for (const [k, inner] of m) {
    const innerOut: Record<string, V> = {};
    for (const [ik, iv] of inner) innerOut[ik] = iv;
    out[k] = innerOut;
  }
  return out;
}

function nestedMapFromRecord<V>(
  r: Record<string, Record<string, V>>,
): Map<string, Map<string, V>> {
  const out = new Map<string, Map<string, V>>();
  for (const [k, inner] of Object.entries(r)) {
    const innerOut = new Map<string, V>();
    for (const [ik, iv] of Object.entries(inner)) innerOut.set(ik, iv);
    out.set(k, innerOut);
  }
  return out;
}

function blendFileRefsFromState(state: AppState): SerializedBlendFileRef[] {
  const refs: SerializedBlendFileRef[] = [];
  for (const [, entry] of state.blendFiles) {
    refs.push({
      id: entry.id,
      fileName: entry.fileName,
      sha256: entry.hash,
      objectNames: entry.file.objects.map((o) => o.name),
    });
  }
  return refs;
}

function groupTransformsFromState(
  state: AppState,
): Record<string, SerializedGroupTransform> {
  const out: Record<string, SerializedGroupTransform> = {};
  for (const [entryId, entry] of state.blendFiles) {
    const gt = entry.groupTransform;
    out[entryId] = {
      position: [gt.position[0], gt.position[1], gt.position[2]],
      rotation: [
        gt.rotation[0],
        gt.rotation[1],
        gt.rotation[2],
        gt.rotation[3],
      ],
      scale: [gt.scale[0], gt.scale[1], gt.scale[2]],
    };
  }
  return out;
}

function encodeReferenceLayers(state: AppState): SerializedReferenceLayer[] {
  return state.referenceLayers.map((l) => ({
    id: l.id,
    name: l.name,
    imageKey: l.imageKey,
    offset: [l.offset[0], l.offset[1]],
    scale: l.scale,
    rotation: l.rotation,
    opacity: l.opacity,
    above: l.above,
    visible: l.visible,
    vanishingLines: l.vanishingLines ? [...l.vanishingLines] : [],
    groundPlaneZ: l.groundPlaneZ ?? 0,
    liveSolve: l.liveSolve ?? false,
    referenceDistance: l.referenceDistance
      ? { ...l.referenceDistance }
      : undefined,
  }));
}

function encodeCollectionCutterRef(
  ref: Collection["cutterRef"] | undefined,
): SerializedCollectionCutterRef | null {
  if (!ref) return null;
  return {
    hostObjectId: ref.hostObjectId,
    cutterObjectIds: [...ref.cutterObjectIds],
  };
}

function decodeCollectionCutterRef(
  c: SerializedCollection,
): Collection["cutterRef"] | undefined {
  const ref = c.cutterRef;
  if (!ref || typeof ref.hostObjectId !== "string") return undefined;
  if (ref.hostObjectId.length === 0) return undefined;

  const memberIds = new Set(c.objectIds);
  if (!memberIds.has(ref.hostObjectId)) return undefined;

  const cutterObjectIds = new Set<string>();
  for (const objectId of ref.cutterObjectIds ?? []) {
    if (
      typeof objectId === "string" &&
      objectId.length > 0 &&
      objectId !== ref.hostObjectId &&
      memberIds.has(objectId)
    ) {
      cutterObjectIds.add(objectId);
    }
  }
  if (cutterObjectIds.size === 0) return undefined;

  return {
    hostObjectId: ref.hostObjectId,
    cutterObjectIds,
  };
}

function encodeCollections(state: AppState): SerializedCollection[] {
  const out: SerializedCollection[] = [];
  for (const c of state.collections.values()) {
    out.push({
      id: c.id,
      name: c.name,
      transform: {
        position: [
          c.transform.position[0],
          c.transform.position[1],
          c.transform.position[2],
        ],
        rotation: [
          c.transform.rotation[0],
          c.transform.rotation[1],
          c.transform.rotation[2],
          c.transform.rotation[3],
        ],
        scale: [
          c.transform.scale[0],
          c.transform.scale[1],
          c.transform.scale[2],
        ],
      },
      objectIds: [...c.objectIds],
      cutterRef: encodeCollectionCutterRef(c.cutterRef),
      generatorRef: c.generatorRef
        ? encodeCollectionGeneratorRef(c.generatorRef)
        : null,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// captureSceneDocument
// ---------------------------------------------------------------------------

/**
 * Walk live `AppState` and produce a `RawSceneDocument`.
 *
 * `imported` scene objects are filtered out — they aren't owned by the scene
 * file. Geometry Float32Arrays remain in their live form;
 * the persistence worker base64-encodes them downstream.
 */
export function captureSceneDocument(state: AppState): RawSceneDocument {
  const now = Date.now();
  return {
    magic: POSEREF_MAGIC,
    version: POSEREF_VERSION,
    kind: "scene",
    createdAt: state.sceneCreatedAt ?? now,
    modifiedAt: now,

    blendFileRefs: blendFileRefsFromState(state),
    groupTransforms: groupTransformsFromState(state),

    sceneObjects: [...state.sceneObjects.values()].filter((o) => !o.imported),

    materials: [...state.materials.values()],

    shapeKeyWeights: nestedRecordFromMap(state.shapeKeyWeights),
    visibility: recordFromMap(state.visibility),
    renderVisibility: recordFromMap(state.renderVisibility),
    shadingOverrides: recordFromMap(state.shadingOverrides),
    modifierOverrides: recordFromMap(state.modifierOverrides),
    favoriteShapeKeys: [...state.favoriteShapeKeys],

    focalLength: state.focalLength,
    cameraBookmarks: [...state.cameraBookmarks],
    cameraPosition: state.cameraPosition ?? null,
    cameraTarget: state.cameraTarget ?? null,

    // SavedInkingView objects are already JSON-safe (strings + numbers):
    // `rasterPng` and `thumbnail` are data URLs, camera state is primitive
    // tuples. Shallow-clone the tuple fields to decouple from the live store.
    savedInkingViews: state.savedInkingViews.map((v) => ({
      ...v,
      cameraPosition: [...v.cameraPosition] as [number, number, number],
      cameraTarget: [...v.cameraTarget] as [number, number, number],
    })),

    referenceLayers: encodeReferenceLayers(state),
    activeReferenceLayerId: state.activeReferenceLayerId,

    renderConfigs: {
      viewport: { ...state.renderConfigs.viewport },
      render: { ...state.renderConfigs.render },
    },
    viewportQuality: { ...state.viewportQuality },
    exportQualityOverrides: { ...state.exportQualityOverrides },
    showTextures: state.showTextures,
    textureMapsEnabled: state.textureMapsEnabled,
    objectXray: state.objectXray,
    showGrid: state.showGrid,

    renderWidth: state.renderWidth,
    renderHeight: state.renderHeight,

    mannequinMorphs: nestedRecordFromMap(state.mannequinMorphs),
    mannequinBonePoses: nestedRecordFromMap(state.mannequinBonePoses),
    pinnedBones: nestedRecordFromMap(state.pinnedBones),
    boneVolumes: nestedRecordFromMap(state.boneVolumes),

    pivotReference: state.pivotReference,
    proportionalEnabled: state.proportionalEnabled,
    proportionalRadius: state.proportionalRadius,
    proportionalFalloff: state.proportionalFalloff,
    snapEnabled: state.snapEnabled,
    snapTarget: state.snapTarget,
    snapWithRotation: state.snapWithRotation,
    surfaceProjectIndividual: state.surfaceProjectIndividual,

    collections: encodeCollections(state),

    cursor3D: {
      position: [
        state.cursor3D.position[0],
        state.cursor3D.position[1],
        state.cursor3D.position[2],
      ],
    },

    // Scene-level sketch state — projected (surface-locked) and screenspace
    // strokes. Always present in v5+ documents (empty layer list when
    // nothing has been sketched).
    sketch: state.sketch,
  };
}

// ---------------------------------------------------------------------------
// captureSessionDocument
// ---------------------------------------------------------------------------

export interface SessionMetadata {
  blendFileName: string | null;
  blendFileHash: string | null;
  currentSceneFileName: string | null;
  sceneDirty: boolean;
  /** Cloud library item this session is backing up, or null for blank sessions. */
  libraryItemId: string | null;
}

export function captureSessionDocument(
  state: AppState,
  metadata: SessionMetadata,
): RawSessionDocument {
  const scene = captureSceneDocument(state);
  return {
    ...scene,
    kind: "session",
    blendFileName: metadata.blendFileName,
    blendFileHash: metadata.blendFileHash,
    currentSceneFileName: metadata.currentSceneFileName,
    sceneDirty: metadata.sceneDirty,
    libraryItemId: metadata.libraryItemId,
  };
}

// ---------------------------------------------------------------------------
// applySceneDocument
// ---------------------------------------------------------------------------

/**
 * Patch live `AppState` with the contents of a decoded scene document.
 *
 * Returns a partial state suitable for `setState(...)`. Restores every
 * persisted field — there are no silent drops by design.
 *
 * Restoring `blendFiles.groupTransform` requires the entries to already
 * be loaded; capture stores per-entry transforms keyed by entry id, and
 * apply walks the live entries to merge in the saved values.
 */
export interface ApplySceneDocumentResult {
  patch: Partial<AppState>;
  /** Objects that failed structural validation and were skipped on apply. */
  omittedObjects: SceneObjectValidationIssue[];
}

export function applySceneDocument(
  doc: RawSceneDocument | RawSessionDocument,
  state: AppState,
): ApplySceneDocumentResult {
  ensureRenderConfigs(doc);
  const patch: Partial<AppState> = {};
  const omittedObjects: SceneObjectValidationIssue[] = [];

  // Group transforms — only the entries that are currently loaded.
  if (state.blendFiles.size > 0) {
    const newFiles = new Map(state.blendFiles);
    for (const [entryId, entry] of state.blendFiles) {
      const saved = doc.groupTransforms[entryId];
      if (saved) {
        newFiles.set(entryId, {
          ...entry,
          groupTransform: {
            position: [saved.position[0], saved.position[1], saved.position[2]],
            rotation: [
              saved.rotation[0],
              saved.rotation[1],
              saved.rotation[2],
              saved.rotation[3],
            ],
            scale: [saved.scale[0], saved.scale[1], saved.scale[2]],
          },
        });
      }
    }
    patch.blendFiles = newFiles;
  }

  // Shape key weights
  const weights = new Map<string, Map<string, number>>();
  for (const [setName, kw] of Object.entries(doc.shapeKeyWeights)) {
    if (state.shapeKeyWeights.has(setName)) {
      weights.set(setName, new Map(Object.entries(kw)));
    }
  }
  for (const [setName, kw] of state.shapeKeyWeights) {
    if (!weights.has(setName)) weights.set(setName, kw);
  }
  patch.shapeKeyWeights = weights;
  if (state.driverGraph) state.driverGraph.evaluate(weights);

  // Visibility
  patch.visibility = new Map(Object.entries(doc.visibility));

  // Render visibility
  patch.renderVisibility = new Map(Object.entries(doc.renderVisibility));

  // Shading overrides
  const shadingOverrides = new Map<string, ShadingMode>();
  for (const [k, v] of Object.entries(doc.shadingOverrides)) {
    shadingOverrides.set(k, v as ShadingMode);
  }
  patch.shadingOverrides = shadingOverrides;

  // Modifier overrides
  const modifierOverrides = new Map<string, ModifierOverride[]>();
  for (const [k, v] of Object.entries(doc.modifierOverrides)) {
    modifierOverrides.set(k, v);
  }
  patch.modifierOverrides = modifierOverrides;

  // Favorites
  patch.favoriteShapeKeys = new Set(doc.favoriteShapeKeys);

  // Camera
  patch.focalLength = doc.focalLength;
  patch.cameraBookmarks = [...doc.cameraBookmarks];
  if (doc.cameraPosition) patch.cameraPosition = doc.cameraPosition;
  if (doc.cameraTarget) patch.cameraTarget = doc.cameraTarget;

  // Saved inking views — optional field for forward-compatibility with files
  // written before the feature existed. Tuples are cloned so later mutations
  // in the store never reach back into the document.
  patch.savedInkingViews = (doc.savedInkingViews ?? []).map((v) => ({
    ...v,
    cameraPosition: [...v.cameraPosition] as [number, number, number],
    cameraTarget: [...v.cameraTarget] as [number, number, number],
  }));

  // Reference image layers — imageUrl is resolved lazily after load.
  patch.referenceLayers = doc.referenceLayers.map((l) => ({
    id: l.id,
    name: l.name,
    imageKey: l.imageKey,
    imageUrl: "",
    offset: [l.offset[0], l.offset[1]],
    scale: l.scale,
    rotation: l.rotation,
    opacity: l.opacity,
    above: l.above,
    visible: l.visible,
    vanishingLines:
      l.vanishingLines.length > 0 ? [...l.vanishingLines] : undefined,
    groundPlaneZ: l.groundPlaneZ !== 0 ? l.groundPlaneZ : undefined,
    liveSolve: l.liveSolve || undefined,
    referenceDistance: l.referenceDistance
      ? { ...l.referenceDistance }
      : undefined,
  }));
  patch.activeReferenceLayerId = doc.activeReferenceLayerId;

  // Viewport — render configs per scope + global overlay/texture toggles.
  patch.renderConfigs = {
    viewport: { ...doc.renderConfigs.viewport },
    render: { ...doc.renderConfigs.render },
  };
  patch.viewportQuality = { ...doc.viewportQuality };
  patch.exportQualityOverrides = { ...doc.exportQualityOverrides };
  patch.showTextures = doc.showTextures;
  patch.textureMapsEnabled = doc.textureMapsEnabled;
  patch.objectXray = doc.objectXray ?? false;
  patch.showGrid = doc.showGrid;

  // Render output (pixel-based)
  patch.renderWidth = doc.renderWidth;
  patch.renderHeight = doc.renderHeight;

  // Scene objects — already decoded by the worker. Validate structurally
  // and surface any failures so the caller can prompt the user.
  const validObjects = doc.sceneObjects.filter((o) => {
    const issue = validateSceneObject(o);
    if (issue) omittedObjects.push(issue);
    return issue == null;
  });

  // Material registry
  const materials = new Map<string, MaterialDataBlock>();
  for (const m of doc.materials) {
    materials.set(m.id, {
      id: m.id,
      name: m.name,
      properties: m.properties ?? {},
    } as MaterialDataBlock);
  }
  patch.materials = materials;

  patch.sceneObjects = restoreSceneObjectMap(state.sceneObjects, validObjects);

  // Second pass: curves with a taperObjectName couldn't resolve their
  // reference during per-object decode (the target might not have been
  // decoded yet). Now that the full map is assembled, regenerate those
  // curves so the taper is applied at load time rather than on first edit.
  const taperUsers: string[] = [];
  for (const obj of patch.sceneObjects.values()) {
    if (obj.curveData?.taperObjectName) taperUsers.push(obj.id);
  }
  if (taperUsers.length > 0) {
    let next = patch.sceneObjects;
    const visited = new Set<string>();
    for (const id of taperUsers) {
      next = regenerateCurveGeometryInMap(next, id, undefined, visited);
    }
    patch.sceneObjects = restoreSceneObjectMap(state.sceneObjects, [
      ...next.values(),
    ]);
  }

  // Mannequin state
  patch.mannequinMorphs = nestedMapFromRecord(doc.mannequinMorphs);
  patch.mannequinBonePoses = nestedMapFromRecord(doc.mannequinBonePoses);
  patch.pinnedBones = nestedMapFromRecord(doc.pinnedBones);
  patch.boneVolumes = nestedMapFromRecord(doc.boneVolumes);

  // Editing
  patch.pivotReference = doc.pivotReference;
  patch.proportionalEnabled = doc.proportionalEnabled;
  patch.proportionalRadius = doc.proportionalRadius;
  patch.proportionalFalloff = doc.proportionalFalloff;
  patch.snapEnabled = doc.snapEnabled;
  patch.snapTarget = doc.snapTarget;
  patch.snapWithRotation = doc.snapWithRotation ?? false;
  patch.surfaceProjectIndividual = doc.surfaceProjectIndividual ?? true;

  // Scene-level sketch — projected and screenspace strokes. Required in v5+
  // documents; older versions are rejected at the version gate.
  patch.sketch = {
    layers: doc.sketch.layers.map((l) => ({ ...l, strokes: [...l.strokes] })),
  };

  // 3D cursor — restored position; selection always resets to false.
  if (doc.cursor3D) {
    patch.cursor3D = {
      position: [
        doc.cursor3D.position[0],
        doc.cursor3D.position[1],
        doc.cursor3D.position[2],
      ],
      selected: false,
    };
  } else {
    patch.cursor3D = { position: [0, 0, 0], selected: false };
  }

  // Collections — transform is a required field in v2+ documents. Legacy
  // v1 files without it are rejected at the version gate in the worker.
  const collections = new Map<string, Collection>();
  for (const c of doc.collections) {
    const cutterRef = decodeCollectionCutterRef(c);
    const generatorRef = c.generatorRef
      ? decodeCollectionGeneratorRef(c.generatorRef)
      : undefined;
    const entry: Collection = {
      id: c.id,
      name: c.name,
      transform: {
        position: [
          c.transform.position[0],
          c.transform.position[1],
          c.transform.position[2],
        ],
        rotation: [
          c.transform.rotation[0],
          c.transform.rotation[1],
          c.transform.rotation[2],
          c.transform.rotation[3],
        ],
        scale: [
          c.transform.scale[0],
          c.transform.scale[1],
          c.transform.scale[2],
        ],
      },
      objectIds: new Set(c.objectIds),
    };
    if (cutterRef) entry.cutterRef = cutterRef;
    if (generatorRef) entry.generatorRef = generatorRef;
    collections.set(c.id, entry);
  }
  patch.collections = collections;

  // Scene-level metadata
  patch.sceneCreatedAt = doc.createdAt;

  // Reset transient mode state — loading a scene drops in-flight selections,
  // edit mode, and any pending command operator.
  patch.selectedObjectTargets = [];
  patch.editMode = null;
  patch.mode = "navigate";
  patch.commandBuffer = null;
  patch.commandPreSnapshot = null;
  patch.commandCancelSnapshot = null;

  return { patch, omittedObjects };
}
